import { useEffect } from "react";
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { selectAllAccounts } from "@/entities/accounts/table";
import { ReduxRootState } from "@/types/redux-store";
import { updateQueueSettings } from "api/account-queue";
import {
  selectCurrentQueueAccountId,
  selectCurrentQueueSettingsPayload,
} from "./queue-managing-model";

export type CopyQueueToAnotherAccountState = {
  selectedAccountId: string | null;
  pending: boolean;
};

const featureKey = "copyQueueToAnotherAccount" as const;

const initialState: CopyQueueToAnotherAccountState = {
  selectedAccountId: null,
  pending: false,
};

function getFeatureState(
  state: ReduxRootState
): CopyQueueToAnotherAccountState {
  return (
    (
      state as ReduxRootState & {
        [featureKey]?: CopyQueueToAnotherAccountState;
      }
    )[featureKey] ?? initialState
  );
}

export const copyQueueToAnotherAccount = createAsyncThunk(
  "copyQueueToAnotherAccount/submit",
  async (_: void, thunkApi) => {
    const state = thunkApi.getState() as ReduxRootState;
    const selectedAccountId = getFeatureState(state).selectedAccountId;

    if (!selectedAccountId) {
      throw new Error("A destination account must be selected");
    }

    const payload = selectCurrentQueueSettingsPayload(state);

    await updateQueueSettings(selectedAccountId, payload);
    toast.success("Account queue was successfully copied");

    return selectedAccountId;
  }
);

const copyQueueToAnotherAccountSlice = createSlice({
  name: featureKey,
  initialState,
  reducers: {
    accountToCopyChanged(state, action: { payload: string | null }) {
      state.selectedAccountId = action.payload;
    },
    reset() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(copyQueueToAnotherAccount.pending, (state) => {
      state.pending = true;
    });
    builder.addCase(copyQueueToAnotherAccount.fulfilled, (state) => {
      state.pending = false;
      state.selectedAccountId = null;
    });
    builder.addCase(copyQueueToAnotherAccount.rejected, (state) => {
      state.pending = false;
    });
  },
});

export const copyQueueToAnotherAccountReducer =
  copyQueueToAnotherAccountSlice.reducer;
export const copyQueueToAnotherAccountActions =
  copyQueueToAnotherAccountSlice.actions;

export const selectCopyQueueToAnotherAccountState = createSelector(
  getFeatureState,
  (copyState) => copyState
);

export const selectCopyQueueDestinationAccounts = createSelector(
  selectAllAccounts,
  selectCurrentQueueAccountId,
  (accounts, sourceAccountId) =>
    accounts.filter((account) => account._id !== sourceAccountId)
);

export const selectCopyQueueModel = createSelector(
  selectCopyQueueToAnotherAccountState,
  selectCopyQueueDestinationAccounts,
  (copyState, accounts) => ({
    selectedAccount: copyState.selectedAccountId,
    accounts,
    submitting: copyState.pending,
  })
);

export function useCopyQueueScope() {
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(copyQueueToAnotherAccountActions.reset());
    };
  }, [dispatch]);
}

export function useCopyQueueModel() {
  return useSelector(selectCopyQueueModel);
}
